/**
 * LAUNCH STATE — telling a brand-new visitor apart from a member coming home.
 *
 * WHY THIS EXISTS (Founder TestFlight, October 2026): an existing member reopened Arvexis on their
 * iPhone and was shown the public landing page, as though they had never signed in. Two causes, both
 * fixable here: the access cookie lives 15 minutes while the refresh cookie lives 7 days, and boot
 * never spent the refresh; and `/` rendered acquisition content regardless of who was asking.
 *
 * WHAT IS STORED: a single flag saying "this device has signed in before". Nothing else.
 * NO tokens, NO email, NO name, NO member content. The session itself remains an httpOnly cookie
 * that JavaScript cannot read, and that is deliberately unchanged. The flag's only job is to decide
 * which unauthenticated screen is the kind one — "Welcome back" rather than a sales pitch.
 *
 * Signing out clears it: a member who explicitly leaves is entitled to be treated as a stranger again.
 */

const RETURNING_MEMBER_KEY = 'arvexis_returning_member';

// Storage can throw (Safari private mode, disabled storage). A flag is never worth a crash.
export const markReturningMember = () => {
  try { localStorage.setItem(RETURNING_MEMBER_KEY, '1'); } catch (e) { /* not essential */ }
};

export const clearReturningMember = () => {
  try { localStorage.removeItem(RETURNING_MEMBER_KEY); } catch (e) { /* not essential */ }
};

export const hasAuthenticatedBefore = () => {
  try { return localStorage.getItem(RETURNING_MEMBER_KEY) === '1'; } catch (e) { return false; }
};

/**
 * Was this a real authentication refusal, or did the request never get an answer?
 * A 401/403 is the server saying no. Anything else — offline, DNS, timeout, 5xx — is not a logout,
 * and must never be allowed to look like one.
 */
export const isAuthFailure = (error) => {
  const status = error?.response?.status;
  return status === 401 || status === 403;
};

/**
 * The four states Arvexis can open in.
 *
 * 'authenticated'      — a valid session; go straight in, no landing page.
 * 'first-time'         — never signed in on this device; the public welcome is correct.
 * 'session-expired'    — signed in before, but the server refused; show "Welcome back", not a pitch.
 * 'connection-unknown' — we could not reach the server. NOT a logout. A returning member is still
 *                        treated as a member and offered sign-in; a stranger still sees the welcome.
 */
export const resolveLaunchState = ({ authenticated = false, reason = null, returning = false } = {}) => {
  if (authenticated) return 'authenticated';
  if (reason === 'network') return returning ? 'connection-unknown' : 'first-time';
  if (reason === 'auth') return returning ? 'session-expired' : 'first-time';
  return returning ? 'session-expired' : 'first-time';
};

/** Which unauthenticated screen that state implies. */
export const showsPublicLanding = (launchState) => launchState === 'first-time';
